export default defineNuxtRouteMiddleware(async (to) => {
	const [slugSegment, pageSegment] = (to.params.newsTitle as string[] | undefined) ?? []

	if (!slugSegment) {
		throw createError({ statusCode: 404 })
	}

	const newsStore = useNewsStore()

	// 1. Global pagination page: /blog/2, /blog/3, ...
	if (/^\d+$/.test(slugSegment)) {
		if (pageSegment) {
			throw createError({ statusCode: 404 })
		}
		setPageLayout('news-catalog')
		newsStore.setNewsPageContext({ type: 'pagination', page: Number(slugSegment) })
		return
	}

	// Category listings paginate as /blog/category-slug/2, /blog/category-slug/3, ...
	// Posts don't have sub-pages, so a page segment rules out the post lookup below.
	if (pageSegment && !/^\d+$/.test(pageSegment)) {
		throw createError({ statusCode: 404 })
	}
	const page = pageSegment ? Number(pageSegment) : 1

	const i18nService = usei18nService()
	const localeLang = i18nService.getLocaleLang()

	// 2 & 3. The segment is either a post slug or a category slug — resolve both in parallel
	// instead of trying the post first and only falling back to category on a 404. That old
	// sequential shape meant every category page paid for a guaranteed-to-fail post lookup
	// before even starting the real one; this halves the worst-case latency and collapses two
	// separate awaited try/catches into a single settle point.
	const [postResult, categoryResult] = await Promise.allSettled([
		pageSegment ? Promise.resolve(null) : ApiService.fetchNewsBySlug(slugSegment, localeLang),
		ApiService.fetchCategoryBySlug(slugSegment, localeLang),
	])

	if (postResult.status === 'rejected' && postResult.reason?.status !== 404) {
		throw postResult.reason
	}
	if (postResult.status === 'fulfilled' && postResult.value) {
		newsStore.setNewsPageContext({ type: 'post', post: postResult.value })
		return
	}

	if (categoryResult.status === 'rejected' && categoryResult.reason?.status !== 404) {
		throw categoryResult.reason
	}
	const category = categoryResult.status === 'fulfilled' ? categoryResult.value : null
	if (category && category.language === localeLang.toLowerCase()) {
		setPageLayout('news-catalog')
		newsStore.setNewsPageContext({ type: 'category', category, categorySlug: slugSegment, page })
		return
	}

	throw createError({ statusCode: 404 })
})
